import type { ReactElement } from "react";
import { Admin } from "./pages/Admin";
import { AdminDepartmentResponses } from "./pages/AdminDepartmentResponses";
import { AdminWorkspaceDetail } from "./pages/AdminWorkspaceDetail";
import { AdminWorkspaceNew } from "./pages/AdminWorkspaceNew";
import { AdminUsers } from "./pages/AdminUsers";
import { Contributor } from "./pages/Contributor";
import { ContributorDepartment } from "./pages/ContributorDepartment";
import { CustomerValueModel } from "./pages/CustomerValueModel";
import { DealWinEngine } from "./pages/DealWinEngine";
import { DepartmentNew } from "./pages/DepartmentNew";
import { DepartmentResponses } from "./pages/DepartmentResponses";
import { DepartmentWeighting } from "./pages/DepartmentWeighting";
import { Departments } from "./pages/Departments";
import { Home } from "./pages/Home";
import { Invite } from "./pages/Invite";
import { LegalPage } from "./pages/LegalPage";
import { Login } from "./pages/Login";
import { AiSummary } from "./pages/AiSummary";
import { Pricing } from "./pages/Pricing";
import { SetupAccount } from "./pages/SetupAccount";
import { SetupPassword } from "./pages/SetupPassword";
import { Workspace } from "./pages/Workspace";
import { WorkspaceSetup } from "./pages/WorkspaceSetup";
import { Layout } from "./components/Layout";
import { parseContributorDepartmentId } from "./lib/contributorRoutes";
import { getDemoSession, getRoleStartPath, userHasRole } from "./lib/demoAuth";
import { languageFromPathname, stripLanguagePrefix, t, useWorkspaceLanguage, withLanguagePrefix } from "./lib/i18n";
import type { Role } from "./types";

function LoginRequired() {
  const session = getDemoSession();
  const language = useWorkspaceLanguage();
  const labels = t(language).layout;

  if (session) {
    return (
      <Layout
        eyebrow={labels.noAccess.eyebrow}
        title={labels.noAccess.title}
        description={labels.noAccess.description}
      >
        <div className="hero-actions">
          <a className="button button-primary" href={withLanguagePrefix(getRoleStartPath(session.role), language)}>
            {labels.noAccess.cta}
          </a>
        </div>
      </Layout>
    );
  }

  return (
    <Layout
      eyebrow={labels.loginRequired.eyebrow}
      title={labels.loginRequired.title}
      description={labels.loginRequired.description}
    >
      <div className="hero-actions">
        <a className="button button-primary" href={withLanguagePrefix("/login", language)}>
          {labels.loginRequired.cta}
        </a>
      </div>
    </Layout>
  );
}

export function App() {
  const language = languageFromPathname(window.location.pathname);
  const path = stripLanguagePrefix(window.location.pathname).replace(/\/$/, "") || "/";
  const session = getDemoSession();

  document.documentElement.lang = language;

  function requireRole(allowedRoles: Role[], page: ReactElement) {
    if (!userHasRole(session, allowedRoles)) return <LoginRequired />;
    return page;
  }

  if (path === "/") return <Home />;
  if (path === "/pricing" || path === "/buy") return <Pricing />;
  if (path === "/login") return <Login />;
  if (path === "/setup-account") return <SetupAccount />;
  if (path === "/setup-password") return <SetupPassword />;
  if (path === "/terms" && language === "de") {
    window.location.replace("/en/terms");
    return <LegalPage kind="terms" />;
  }
  if (path === "/privacy" && language === "de") {
    window.location.replace("/en/privacy");
    return <LegalPage kind="privacy" />;
  }
  if (path === "/terms" || path === "/agb") return <LegalPage kind="terms" />;
  if (path === "/privacy" || path === "/datenschutz") return <LegalPage kind="privacy" />;
  if (path === "/admin") return requireRole(["hauffe_admin"], <Admin />);
  if (path === "/admin/users") return requireRole(["hauffe_admin"], <AdminUsers />);
  if (path === "/admin/workspaces/new") return requireRole(["hauffe_admin"], <AdminWorkspaceNew />);
  if (path.startsWith("/admin/workspaces/") && path.includes("/departments/") && path.endsWith("/responses")) {
    const parts = path.split("/").filter(Boolean);
    const workspaceId = parts[2] ?? "";
    const departmentId = parts[4] ?? "";
    return requireRole(["hauffe_admin"], <AdminDepartmentResponses workspaceId={workspaceId} departmentId={departmentId} />);
  }
  if (path.startsWith("/admin/workspaces/")) return requireRole(["hauffe_admin"], <AdminWorkspaceDetail />);
  if (path === "/workspace") return requireRole(["hauffe_admin", "company_owner"], <Workspace />);
  if (path === "/workspace/plans") return requireRole(["hauffe_admin", "company_owner"], <Workspace section="plans" />);
  if (path === "/workspace/ai-summary") {
    window.location.replace(withLanguagePrefix("/workspace/hauffe-os-summary", language));
    return <LoginRequired />;
  }
  if (path === "/workspace/hauffe-os-summary") return requireRole(["hauffe_admin", "company_owner"], <AiSummary />);
  if (path === "/workspace/customer-value-model" || path === "/workspace/customer-value") {
    return requireRole(["hauffe_admin", "company_owner"], <CustomerValueModel />);
  }
  if (path === "/workspace/deal-win-engine") return requireRole(["hauffe_admin", "company_owner"], <DealWinEngine />);
  if (path.startsWith("/workspace/deal-win-engine/")) {
    const dealId = path.split("/").filter(Boolean).at(-1) ?? "";
    return requireRole(["hauffe_admin", "company_owner"], <DealWinEngine dealId={dealId} />);
  }
  if (path === "/workspace/setup") return requireRole(["hauffe_admin", "company_owner"], <WorkspaceSetup />);
  if (path === "/workspace/departments") return requireRole(["hauffe_admin", "company_owner"], <Departments />);
  if (path.startsWith("/workspace/departments/") && path.endsWith("/input")) {
    const departmentId = path.split("/").filter(Boolean).at(-2) ?? "";
    return requireRole(["hauffe_admin", "company_owner"], <ContributorDepartment departmentId={departmentId} audience="owner" />);
  }
  if (path.startsWith("/workspace/departments/") && path.endsWith("/weighting")) {
    const departmentId = path.split("/").filter(Boolean).at(-2) ?? "";
    return requireRole(["hauffe_admin", "company_owner"], <DepartmentWeighting departmentId={departmentId} audience="owner" />);
  }
  if (path.startsWith("/workspace/departments/") && path.endsWith("/responses")) {
    const departmentId = path.split("/").filter(Boolean).at(-2) ?? "";
    return requireRole(["hauffe_admin", "company_owner"], <DepartmentResponses departmentId={departmentId} />);
  }
  if (path === "/workspace/departments/new") return requireRole(["hauffe_admin", "company_owner"], <DepartmentNew />);
  if (path.startsWith("/invite/")) {
    const inviteToken = path.split("/").filter(Boolean).at(-1) ?? "";
    return <Invite inviteToken={inviteToken} />;
  }
  if (path === "/contributor") return requireRole(["department_contributor"], <Contributor />);
  if (path.startsWith("/contributor/departments/") && path.endsWith("/weighting")) {
    const departmentId = path.split("/").filter(Boolean).at(-2) ?? "";
    return requireRole(["department_contributor"], <DepartmentWeighting departmentId={departmentId} />);
  }
  if (path.startsWith("/contributor/")) {
    const departmentId = parseContributorDepartmentId(path);
    if (!departmentId) return requireRole(["department_contributor"], <Contributor />);
    return requireRole(["department_contributor"], <ContributorDepartment departmentId={departmentId} />);
  }

  return <Home />;
}
